"use client";

import { useEffect, useRef } from "react";

/**
 * Like `useRevealOnce`, but it keeps observing: `is-visible` goes on every
 * time the element comes back into view and comes off once it has left
 * completely, so the section's entrance plays again on the way back up rather
 * than only the first time it is reached.
 *
 * Taking the class off is what makes the replay possible at all — a CSS
 * keyframe only restarts when the rule that started it is removed and
 * re-added — and it is only ever taken off while the element is fully out of
 * frame, so the reader never catches it resetting.
 */
export function useRevealInView<T extends HTMLElement>(
  /** How much of the element must be on screen before it fires. */
  threshold = 0.25,
) {
  const ref = useRef<T>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // No IntersectionObserver (or reduced motion): show the final state now.
    if (
      typeof IntersectionObserver === "undefined" ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      el.classList.add("is-visible");
      return;
    }

    // Same clamp as `useRevealOnce`: an element taller than `1 / threshold`
    // viewports can never reach the ratio it asked for, so cap the ask at
    // what a viewport-and-a-bit of it is actually worth.
    const reachable = window.innerHeight / el.getBoundingClientRect().height;
    const effective = Math.max(Math.min(threshold, reachable * 0.6), 0.01);

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.intersectionRatio >= effective) {
          el.classList.add("is-visible");
        } else if (!entry.isIntersecting) {
          el.classList.remove("is-visible");
        }
      },
      // Both edges: `effective` is the arrival, 0 is the moment it is gone.
      { threshold: [0, effective] },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [threshold]);

  return ref;
}
